import { SparklesFilled } from '@wix/wix-ui-icons-common';
import {
  Box,
  Button,
  IconButton,
  WixDesignSystemProvider,
} from '@wix/design-system';
import { DIGORIFrame } from './DIGORIFrame';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { getAssistantTypeFromURL, getOpenStateFromURL } from './embed-utils';
import { clog } from '@/utils/clog';
import { DIGORBI } from '@/utils/bi';
import { useKeyboardShortcuts } from '@/components/ChatWidgetWrapper/useKeyboardShortcuts';
import { useBridgeMessages } from '@/components/ChatWidgetWrapper/useBridgeMessages';
import {
  DIGORState,
  readStateFromLocalStorage,
  resetState,
  saveStateToLocalStorage,
} from '@/utils/local-storage';
import { ASSISTANT_CONFIG } from '@/utils/constants';

const getDimensions = (open: boolean, uiType: string, fullScreen = false) => {
  const fullScreenAndOpen = fullScreen && open;
  let bottom = fullScreenAndOpen ? '50%' : '15px';
  let right = fullScreenAndOpen ? '50%' : '15px';
  let transform = fullScreenAndOpen ? 'translate(50%, 50%)' : 'none';

  const isSmallWidth = window.innerWidth < 768;
  const isMediumWidth = window.innerWidth <= 1800;

  const minWidth = !fullScreenAndOpen ? '320px' : 'auto';

  if (isSmallWidth && open) {
    bottom = '20px';
    right = '50%';
    transform = 'translate(50%, 0%)';
  }

  /*if (isSmallWidth && !open) {
    right = '13px';
    bottom = '13px';
  }*/

  const openWidth = fullScreenAndOpen
    ? '80vw'
    : uiType === 'chat-widget'
    ? isSmallWidth
      ? '330px'
      : '500px'
    : isSmallWidth
    ? '400px'
    : isMediumWidth
    ? 'calc(3/7 * 100vw - 300px)'
    : '600px';
  const width = open ? openWidth : isSmallWidth ? '40px' : 'auto';

  const openHeight = isSmallWidth ? '70vh' : '90vh';

  const height = open ? openHeight : isSmallWidth ? '45px' : '60px';

  const buttonSize = isSmallWidth ? 'small' : undefined;

  return {
    minWidth,
    isSmallWidth,
    buttonSize,
    transform,
    bottom,
    right,
    width,
    height,
    openWidth,
    openHeight,
  };
};

export const useIsMount = () => {
  const isMountRef = useRef(true);
  useEffect(() => {
    isMountRef.current = false;
  }, []);
  return isMountRef.current;
};

export const DIGORWrapper = ({
  assistantType,
  uiType = 'ai-widget',
  stream = 'true',
  showOpenButton = 'true',
}: {
  assistantType?: string;
  uiType?: string;
  stream?: string;
  showOpenButton?: string;
}) => {
  const isMount = useIsMount();
  const isStartingOpen = getOpenStateFromURL();
  const [open, setOpen] = React.useState(isStartingOpen);

  const [showOpenButtonState, setShowOpenButtonState] =
    React.useState(showOpenButton);

  const assistantTypeFromURL = getAssistantTypeFromURL();
  const theAssistantType = assistantType || assistantTypeFromURL;
  const assistantConfig = ASSISTANT_CONFIG[theAssistantType as any];
  const niceName = assistantConfig?.niceName || assistantType;
  const biRef = useRef<DIGORBI>(new DIGORBI(theAssistantType));
  const [fullScreen, setFullScreen] = React.useState(isStartingOpen);

  useLayoutEffect(() => {
    (async () => {
      const currentState = await readStateFromLocalStorage<DIGORState>();
      if (currentState?.open) {
        setOpen(true);
      }

      if (typeof window !== 'undefined') {
        (window as any).DIGOR = {
          open: () => {
            setOpen(true);
          },
          close: () => {
            setOpen(false);
          },
          hide: () => {
            setShowOpenButtonState('false');
          },
          show: () => {
            setShowOpenButtonState('true');
          },
        };
      }
    })();

    // cleanup
    return () => {
      (window as any).DIGOR = undefined;
    };
  }, []);

  const { sendMessageToIframe } = useBridgeMessages({
    onMessageFromIframe: (message) => {
      if (message.type === 'd2p:digor:toggle') {
        setOpen((prev) => !prev);
        sendMessageToIframe('p2d:digor:toggle', { open: !open });
      }
      if (message.type === 'd2p:digor:open') {
        setOpen(true);
      }
      if (message.type === 'd2p:digor:save-state') {
        saveStateToLocalStorage({ ...message });
      }
      if (message.type === 'd2p:digor:reset-state') {
        resetState();
      }
    },
  });

  useEffect(() => {
    if (!isMount) {
      saveStateToLocalStorage({ open });
    }
  }, [open]);

  useKeyboardShortcuts((e) => {
    if (e.metaKey && e.key === '.') {
      setOpen((prev) => !prev);
      console.log('sending-toggle', !open);
      sendMessageToIframe('p2d:digor:toggle', { open: !open });
    }
  });

  clog('digor-wrapper: rendering', assistantType, open);

  if (!theAssistantType) {
    clog('digor-wrapper: no assistant type');
    return null;
  }

  const {
    buttonSize,
    transform,
    bottom,
    right,
    width,
    height,
    minWidth,
    openHeight,
    openWidth,
    isSmallWidth,
  } = getDimensions(open, uiType, fullScreen);

  clog('digor-wrapper: dimensions', width, height);

  //  animation={open ? '' : 'pulsingGlow 2.2s infinite ease-in-out'} // pulsingGlow / 2.5s / infinite / ease-in-out

  return (
    <WixDesignSystemProvider
      features={{
        newColorsBranding: true,
      }}
    >
      <Box
        className="DIGORWrapper"
        position="absolute"
        bottom={bottom}
        right={right}
        width={width}
        minWidth={open ? minWidth : undefined}
        transform={transform}
        maxWidth={'90vw'}
        height={height}
        display="flex"
        align="center"
        verticalAlign="middle"
        zIndex="900"
        cursor={open ? 'default' : 'pointer'}
        border={open ? '1px solid #ccc' : 'none'}
        borderRadius={open ? '8px' : '50%'}
        overflow={open ? 'hidden' : undefined}
        boxShadow={open ? '0 0 20px rgba(0, 0, 0, 0.5)' : 'none'}
        backgroundColor={open ? 'white' : undefined}
      >
        {!open && showOpenButtonState !== 'false' ? (
          <div
            onClick={() => {
              setOpen(true);
              biRef.current.open();
              sendMessageToIframe('p2d:digor:toggle', { open: true });
            }}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/*<SparklesFilled color="#3498DB" />*/}
            {!isSmallWidth ? (
              <Button
                className="DIGORWrapperButton"
                priority="secondary"
                skin="ai"
                size={buttonSize as any}
                prefixIcon={<SparklesFilled />}
              >
                {niceName ? `${niceName} Assistant` : `Docs Assistant`}
              </Button>
            ) : (
              <IconButton
                className="DIGORWrapperButton"
                skin="inverted"
                priority="secondary"
                size="medium"
              >
                <SparklesFilled color="#3498DB" />
              </IconButton>
            )}
          </div>
        ) : null}
        <DIGORIFrame
          onClose={() => {
            setOpen(false);
            sendMessageToIframe('p2d:digor:toggle', { open: false });
            biRef.current.close();
          }}
          open={open}
          assistantType={theAssistantType}
          uiType={uiType}
          stream={stream}
          width={openWidth}
          fullScreen={fullScreen}
          toggleFullScreen={() => {
            setFullScreen((prev) => !prev);
            biRef.current.click('full-screen', !fullScreen);
          }}
        />
      </Box>
    </WixDesignSystemProvider>
  );
};
